import { SectionKey } from "../sections"

/**
 * Configuration for a promotion override that maps a section to a prototype bundle.
 */
export interface PromotionOverrideConfig {
  /** The section key being overridden (e.g., "alerts", "assets") */
  sectionKey: string
  /** The prototype ID from the rapid manifest */
  prototypeId: string
  /** Base URL for prototype bundles on S3 */
  bundleBaseUrl: string
  /** Whether this override is currently active */
  enabled: boolean
}

/**
 * Discriminated union representing how a section should be loaded.
 * - "native": load from the npm package (default behavior)
 * - "promoted": load from a prototype bundle on S3
 */
export type SectionResolution =
  | { type: "native" }
  | { type: "promoted"; prototypeId: string; bundleUrl: string }

/**
 * Determines whether a section should load from its native npm package
 * or from a promoted prototype bundle.
 *
 * Never throws — malformed or missing overrides always fall back to native.
 *
 * @param sectionName - The section key to resolve
 * @param promotionOverrides - Map of section keys to override configs, or null/undefined
 * @returns A SectionResolution indicating native or promoted loading
 */
export function resolveSection(
  sectionName: SectionKey,
  promotionOverrides: Record<string, PromotionOverrideConfig> | null | undefined,
): SectionResolution {
  // No overrides configured at all — fall back to native
  if (!promotionOverrides) {
    return { type: "native" }
  }

  const override = promotionOverrides[sectionName]

  // No override for this section — fall back to native
  if (!override) {
    return { type: "native" }
  }

  // Override is disabled — fall back to native
  if (!override.enabled) {
    return { type: "native" }
  }

  // Malformed override: missing or empty prototypeId or bundleBaseUrl — fall back to native
  if (!override.prototypeId || !override.bundleBaseUrl) {
    return { type: "native" }
  }

  // Valid, enabled override — return promoted resolution
  const bundleUrl = `${override.bundleBaseUrl}/${override.prototypeId}/index.js`

  return {
    type: "promoted",
    prototypeId: override.prototypeId,
    bundleUrl,
  }
}
